import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { FeedsService, RSSItem, RSSSourceWithCounts } from '../../services/feedsService';
import PageHeader from '../common/PageHeader';
import FeedItem from './FeedItem';
import './Feeds.css';

interface FeedsProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type StatusFilter = 'all' | 'unread' | 'starred';
type SortOrder = 'desc' | 'asc';

const Feeds: React.FC<FeedsProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<RSSItem[]>([]);
  const [sources, setSources] = useState<RSSSourceWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const LIMIT = 50;

  // Load sources and initial items
  useEffect(() => {
    loadSources();
    loadItems(true);
  }, [selectedSource, statusFilter, sortOrder]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const loadSources = async () => {
    try {
      const sourcesData = await FeedsService.getSources(true);
      setSources(sourcesData);
    } catch (err) {
      console.error('Error loading sources:', err);
    }
  };

  const loadItems = async (reset: boolean = false) => {
    try {
      if (reset) {
        setLoading(true);
        setOffset(0);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const filters: any = {
        limit: LIMIT,
        offset: reset ? 0 : offset,
        sortBy: 'published_date',
        order: sortOrder
      };

      if (selectedSource !== 'all') {
        filters.sourceId = selectedSource;
      }

      if (statusFilter === 'unread') {
        filters.isRead = false;
      } else if (statusFilter === 'starred') {
        filters.isStarred = true;
      }

      const result = await FeedsService.getItems(filters);

      if (reset) {
        setItems(result.items);
        setOffset(LIMIT);
      } else {
        setItems(prev => [...prev, ...result.items]);
        setOffset(prev => prev + LIMIT);
      }

      setHasMore(result.items.length === LIMIT);
    } catch (err) {
      console.error('Error loading items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feed items');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleToggleRead = async (itemId: string, isRead: boolean) => {
    try {
      await FeedsService.updateItem(itemId, { isRead });
      
      // Update local state
      setItems(prev => prev.map(item => 
        item.item_id === itemId ? { ...item, is_read: isRead } : item
      ));

      // Reload sources to update unread counts
      loadSources();
    } catch (err) {
      console.error('Error toggling read status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update item');
    }
  };

  const handleToggleStar = async (itemId: string, isStarred: boolean) => {
    try {
      await FeedsService.updateItem(itemId, { isStarred });
      
      // Update local state
      setItems(prev => prev.map(item => 
        item.item_id === itemId ? { ...item, is_starred: isStarred } : item
      ));
    } catch (err) {
      console.error('Error toggling star status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update item');
    }
  };

  const handleDelete = async (itemId: string) => {
    try {
      await FeedsService.deleteItem(itemId);
      
      // Remove from local state
      setItems(prev => prev.filter(item => item.item_id !== itemId));
      
      // Reload sources to update counts
      loadSources();
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    }
  };

  const handleManageSources = () => {
    navigate('/feeds/sources');
  };

  const getTotalUnreadCount = (): number => {
    return sources.reduce((sum, source) => sum + source.unread_count, 0);
  };

  return (
    <div className="feeds-page">
      <PageHeader 
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">📡 Feeds</h1>}
        leftActions={
          <button 
            className="page-header-icon-btn feeds-back-btn"
            onClick={onClose}
          >
            ← Back
          </button>
        }
        rightActions={
          <div className="feeds-header-actions">
            <div className="feeds-menu-container" ref={menuRef}>
              <button 
                className="feeds-menu-btn"
                onClick={() => setShowMenu(!showMenu)}
                title="Menu"
              >
                ⋮
              </button>
              {showMenu && (
                <div className="feeds-menu-dropdown">
                  <button 
                    className="feeds-menu-item"
                    onClick={handleManageSources}
                  >
                    Manage Sources
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />

      <div className="feeds-content">
        {/* Filters Bar */}
        <div className="feeds-filters">
          <div className="feeds-filter-group">
            <label htmlFor="source-filter">Source:</label>
            <select
              id="source-filter"
              className="feeds-filter-select"
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
            >
              <option value="all">All Sources ({getTotalUnreadCount()} unread)</option>
              {sources.map(source => (
                <option key={source.source_id} value={source.source_id}>
                  {source.source_name} ({source.unread_count} unread)
                </option>
              ))}
            </select>
          </div>

          <div className="feeds-filter-group">
            <label htmlFor="status-filter">Status:</label>
            <select
              id="status-filter"
              className="feeds-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <option value="all">All</option>
              <option value="unread">Unread</option>
              <option value="starred">Starred</option>
            </select>
          </div>

          <div className="feeds-filter-group">
            <label htmlFor="sort-filter">Sort:</label>
            <select
              id="sort-filter"
              className="feeds-filter-select"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
            >
              <option value="desc">Latest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="feeds-error">
            <p>{error}</p>
            <button onClick={() => loadItems(true)}>Retry</button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="feeds-loading">
            <div className="loading-spinner"></div>
            <p>Loading feeds...</p>
          </div>
        ) : items.length === 0 ? (
          /* Empty State */
          <div className="feeds-empty">
            {sources.length === 0 ? (
              <>
                <p>No RSS sources added yet.</p>
                <button 
                  className="feeds-add-source-btn"
                  onClick={handleManageSources}
                >
                  Add Your First Source
                </button>
              </>
            ) : (
              <p>No items found with current filters.</p>
            )}
          </div>
        ) : (
          /* Items List */
          <>
            <div className="feeds-items-list">
              {items.map(item => (
                <FeedItem
                  key={item.item_id}
                  item={item}
                  onToggleRead={handleToggleRead}
                  onToggleStar={handleToggleStar}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="feeds-load-more">
                <button
                  className="feeds-load-more-btn"
                  onClick={() => loadItems(false)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Feeds;

